import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Chip,
    Container,
    Dialog,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { PageHeader } from '../components/PageHeader';
import { ClientForm } from '../components/clients/ClientForm';
import { NewClientDialog } from '../components/clients/NewClientDialog';
import { useClientDetail, useClientList, useClientReference } from '../hooks/useClients';

/**
 * Clients.
 *
 * A list that opens into the four-tab form. The legacy module was a DataTable
 * page plus four separate controllers, each of which re-fetched the client and
 * rendered its own copy of the header — which is why the tabs were full page
 * loads and why the Details tab could show one company name while the Rates tab
 * showed another after an edit.
 *
 * Laid out like the Jobs page: full-width container, filter bar in a card,
 * striped rows with client-side pagination, and Add / Edit as popups. 23 of the
 * 39 production clients are disabled, so Active is the default filter — as a
 * visible button, not a hidden one.
 */

const STATUS_FILTERS = [
    { value: 'Active', label: 'Active' },
    { value: 'Disabled', label: 'Disabled' },
    { value: 'all', label: 'All' },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

export function Clients(): React.JSX.Element {
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<StatusFilter>('Active');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // ?client=<id>&tab=invoicing opens a client straight onto a tab — Create Invoice links here.
    const [searchParams, setSearchParams] = useSearchParams();
    const [selected, setSelected] = useState<number | null>(() => {
        const id = Number(searchParams.get('client'));
        return Number.isInteger(id) && id > 0 ? id : null;
    });
    const initialTab =
        searchParams.get('tab') === 'invoicing' && selected === Number(searchParams.get('client')) ? 3 : 0;
    const [creating, setCreating] = useState(false);
    // Bumped on each open so the dialog starts from a blank form.
    const [createKey, setCreateKey] = useState(0);

    // Xero's OAuth callback lands back here with ?xero=connected|error.
    const xeroResult = searchParams.get('xero');
    const xeroMessage =
        xeroResult === 'connected'
            ? `Xero connected${searchParams.get('tenant') !== null ? ` to ${searchParams.get('tenant') ?? ''}` : ''}.`
            : xeroResult === 'error'
              ? `Xero was not connected: ${searchParams.get('message') ?? 'unknown error'}`
              : null;

    // Disabled clients are only returned when asked for; the Disabled filter
    // then narrows to them in the browser.
    const { clients: fetched, loading, error, reload } = useClientList(search, status !== 'Active');
    const { detail, reload: reloadDetail } = useClientDetail(selected);
    const { xeroOptions, skillsets } = useClientReference();

    const clients = status === 'Disabled' ? fetched.filter((c) => !c.isActive) : fetched;

    const handleSaved = useCallback(() => {
        reloadDetail();
        reload();
    }, [reloadDetail, reload]);

    const handleDeleted = useCallback(() => {
        setSelected(null);
        reload();
    }, [reload]);

    // Clamp rather than reset in an effect: a search that shrinks the list
    // should land on its last page, not an empty one.
    const lastPage = Math.max(0, Math.ceil(clients.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = clients.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Clients" subtitle="View and manage all clients" />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flexWrap="wrap"
                        sx={{ flex: 1 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search name, suburb or ABN..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            slotProps={{
                                input: { startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> },
                            }}
                            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 360 } }}
                        />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            {STATUS_FILTERS.map((f) => (
                                <Button
                                    key={f.value}
                                    size="small"
                                    variant={status === f.value ? 'contained' : 'outlined'}
                                    color={status === f.value ? 'primary' : 'inherit'}
                                    onClick={() => {
                                        setStatus(f.value);
                                        setPage(0);
                                    }}
                                    sx={{ flex: { xs: '1 1 30%', sm: '0 0 auto' } }}
                                >
                                    {f.label}
                                    {status === f.value && !loading ? ` (${String(clients.length)})` : ''}
                                </Button>
                            ))}
                        </Stack>
                    </Stack>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setCreateKey((k) => k + 1);
                            setCreating(true);
                        }}
                    >
                        New Client
                    </Button>
                </Stack>
            </Paper>

            {xeroMessage !== null ? (
                <Alert
                    severity={xeroResult === 'connected' ? 'success' : 'error'}
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setSearchParams({}, { replace: true });
                    }}
                >
                    {xeroMessage}
                </Alert>
            ) : null}

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Company</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>Suburb</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {loading
                                            ? 'Loading clients...'
                                            : search.trim() === ''
                                              ? 'No clients here.'
                                              : `No clients match "${search}".`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageRows.map((c, idx) => (
                                <TableRow
                                    key={c.id}
                                    hover
                                    onClick={() => {
                                        setSelected(c.id);
                                    }}
                                    sx={{ cursor: 'pointer', bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}
                                >
                                    <TableCell sx={{ fontWeight: 600 }}>{c.companyName}</TableCell>
                                    <TableCell>
                                        {[c.firstName, c.lastName].filter((n) => n !== null && n !== '').join(' ') ||
                                            '—'}
                                    </TableCell>
                                    <TableCell>{c.suburbName ?? '—'}</TableCell>
                                    <TableCell>{c.phoneNumber ?? '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={c.isActive ? 'Active' : 'Disabled'}
                                            color={c.isActive ? 'success' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelected(c.id);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={clients.length}
                page={currentPage}
                onPageChange={(_e, p) => {
                    setPage(p);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            <Dialog
                // Opens once the client has loaded, so there is no empty dialog or spinner in between.
                open={selected !== null && detail !== null && detail.client.id === selected}
                onClose={() => {
                    setSelected(null);
                }}
                maxWidth="lg"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                {detail !== null && detail.client.id === selected ? (
                    <ClientForm
                        detail={detail}
                        xeroOptions={xeroOptions}
                        skillsets={skillsets}
                        initialTab={initialTab}
                        onSaved={handleSaved}
                        onDeleted={handleDeleted}
                        onClose={() => {
                            setSelected(null);
                        }}
                    />
                ) : null}
            </Dialog>

            <NewClientDialog
                key={createKey}
                open={creating}
                onCancel={() => {
                    setCreating(false);
                }}
                onCreated={(c) => {
                    setCreating(false);
                    reload();
                    // Straight into Edit Client, where contacts, Xero and rates are added.
                    setSelected(c.id);
                }}
            />
        </Container>
    );
}
